import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminService } from '../../services/adminService';
import { Search, Plus, Edit, Trash2, Eye, Users, TrendingUp, Clock } from 'lucide-react';

const TeamLeaders = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);

  const { data: teamLeaders = [], isLoading } = useQuery({
    queryKey: ['adminTeamLeads'],
    queryFn: AdminService.getTeamLeads,
    staleTime: 5 * 60 * 1000,
  });

  const filteredLeaders = teamLeaders.filter(leader => {
    const matchesSearch = 
      leader.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      leader.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || leader.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    return status === 'active' ? '#059669' : '#6b7280';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Team Leaders</h1>
          <p className="text-gray-600 mt-1">Manage team leaders and departments</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
        >
          <Plus size={20} />
          <span>Add Team Leader</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search team leaders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg border ${
                filterStatus === 'all'
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              All ({teamLeaders.length})
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-lg border ${
                filterStatus === 'active'
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              Active ({teamLeaders.filter(l => l.status === 'active').length})
            </button>
            <button
              onClick={() => setFilterStatus('inactive')}
              className={`px-4 py-2 rounded-lg border ${
                filterStatus === 'inactive'
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              Inactive ({teamLeaders.filter(l => l.status === 'inactive').length})
            </button>
          </div>
        </div>
      </div>

      {/* Team Leaders List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading team leaders...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLeaders.map((leader) => {
            const teamSize = users.filter(u => 
              u.roles?.includes('telecaller') && 
              u.team_lead_id === leader.id
            ).length;

            return (
              <div key={leader.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-semibold text-lg">
                        {leader.name?.charAt(0).toUpperCase() || 'T'}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{leader.name || 'N/A'}</h3>
                      <p className="text-sm text-gray-500">{leader.email || 'N/A'}</p>
                    </div>
                  </div>
                  <span
                    className="px-2 py-1 text-xs font-medium rounded text-white"
                    style={{ backgroundColor: getStatusColor(leader.status) }}
                  >
                    {leader.status || 'Active'}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <Users size={20} className="mx-auto text-gray-400 mb-1" />
                    <p className="text-lg font-bold text-gray-900">{teamSize}</p>
                    <p className="text-xs text-gray-500">Team Size</p>
                  </div>
                  <div className="text-center">
                    <TrendingUp size={20} className="mx-auto text-gray-400 mb-1" />
                    <p className="text-lg font-bold text-gray-900">-</p>
                    <p className="text-xs text-gray-500">Performance</p>
                  </div>
                  <div className="text-center">
                    <Clock size={20} className="mx-auto text-gray-400 mb-1" />
                    <p className="text-lg font-bold text-gray-900">-</p>
                    <p className="text-xs text-gray-500">Last Active</p>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => alert(`Edit ${leader.name}`)}
                    className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center justify-center space-x-2"
                  >
                    <Edit size={16} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => alert(`View details for ${leader.name}`)}
                    className="flex-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center justify-center space-x-2"
                  >
                    <Eye size={16} />
                    <span>View</span>
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete ${leader.name}?`)) {
                        alert('Delete functionality');
                      }
                    }}
                    className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center justify-center space-x-2"
                  >
                    <Trash2 size={16} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filteredLeaders.length === 0 && !isLoading && (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
          <Users size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No team leaders found</p>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Add Team Leader</h2>
            <p className="text-gray-600 mb-4">
              Team leaders are created through the User Management page. 
              Please assign the "teamlead" role to a user to make them a team leader.
            </p>
            <button
              onClick={() => {
                setShowAddModal(false);
                window.location.href = '/users';
              }}
              className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              Go to User Management
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamLeaders;

